import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { QueryClient } from '@tanstack/react-query';
import { redirect } from 'react-router-dom';

import { queryBffContext } from '@/components/app/data/queries/queries';
import { isEssentialsFlow, validateFormState } from '@/components/app/routes/loaders/utils';
import { CheckoutPageRoute, DataStoreKey, EssentialsPageRoute } from '@/constants/checkout';
import { checkoutFormStore } from '@/hooks/useCheckoutFormStore';
import { extractPriceId, getCheckoutPageDetails, getStepFromParams } from '@/utils/checkout';
/**
 * Route loader for Plan Details page.
 *
 * @returns {Promise<Response | null>} Null to continue rendering the route without redirect.
 */
async function planDetailsLoader(): Promise<Response | null> {
  // Plan Details page doesn't require authentication
  return null;
}

/**
 * Route loader for Plan Details Login page.
 *
 * @returns {Promise<Response | null>} Redirects to Plan Details if already authenticated; otherwise null.
 */
async function planDetailsLoginLoader(): Promise<Response | null> {
  const authenticatedUser = getAuthenticatedUser();
  const planDetailsMetadata = checkoutFormStore.getState().formData[DataStoreKey.PlanDetails];
  const redirectToPlanDetails = !planDetailsMetadata.adminEmail;
  if (redirectToPlanDetails || authenticatedUser) {
    // Redirect to PlanDetails if: (1) adminEmail is missing, or (2) user is already authenticated.
    if (isEssentialsFlow()) {
      return redirect(EssentialsPageRoute.PlanDetails);
    }
    return redirect(CheckoutPageRoute.PlanDetails);
  }
  return null;
}

/**
 * Route loader for Plan Details Register page.
 *
 * @returns {Promise<Response | null>} Redirects to Plan Details if already authenticated; otherwise null.
 */
async function planDetailsRegisterLoader(): Promise<Response | null> {
  const authenticatedUser = getAuthenticatedUser();
  const planDetailsMetadata = checkoutFormStore.getState().formData[DataStoreKey.PlanDetails];
  const redirectToPlanDetails = !(
    planDetailsMetadata.adminEmail && planDetailsMetadata.fullName && planDetailsMetadata.country
  );
  if (redirectToPlanDetails || authenticatedUser) {
    // Redirect to PlanDetails if: (1) required metadata is missing, or (2) user is already authenticated.
    if (isEssentialsFlow()) {
      return redirect(EssentialsPageRoute.PlanDetails);
    }
    return redirect(CheckoutPageRoute.PlanDetails);
  }

  return null;
}

/**
 * Route loader for Account Details page.
 *
 * @param {QueryClient} queryClient - TanStack Query client to ensure context and session queries.
 * @returns {Promise<Response | null>} Redirects if prerequisites fail; otherwise null to proceed.
 */
async function accountDetailsLoader(queryClient: QueryClient): Promise<Response | null> {
  const authenticatedUser = getAuthenticatedUser();
  if (!authenticatedUser) {
    // If the user is NOT authenticated, redirect to PlanDetails Page.
    if (isEssentialsFlow()) {
      return redirect(EssentialsPageRoute.PlanDetails);
    }
    return redirect(CheckoutPageRoute.PlanDetails);
  }

  const contextMetadata: CheckoutContextResponse = await queryClient.ensureQueryData(
    queryBffContext(authenticatedUser?.userId || null),
  );
  const { fieldConstraints, pricing } = contextMetadata;

  const stripePriceId = extractPriceId(pricing);
  if (!stripePriceId) {
    return redirect(CheckoutPageRoute.PlanDetails);
  }

  const {
    valid,
    invalidRoute,
  } = await validateFormState({
    checkoutStep: 'AccountDetails',
    constraints: fieldConstraints,
    stripePriceId,
  });
  if (!valid && invalidRoute) {
    return redirect(invalidRoute);
  }

  return null;
}

/**
 * Route loader for Billing Details page.
 *
 * @param {QueryClient} queryClient - TanStack Query client to ensure context and checkout session queries.
 * @returns {Promise<Response | null>} Redirects to appropriate route if validation fails; otherwise null.
 */
async function billingDetailsLoader(queryClient: QueryClient): Promise<Response | null> {
  const authenticatedUser = getAuthenticatedUser();
  if (!authenticatedUser) {
    // If the user is NOT authenticated, redirect to PlanDetails Page.
    if (isEssentialsFlow()) {
      return redirect(EssentialsPageRoute.PlanDetails);
    }
    return redirect(CheckoutPageRoute.PlanDetails);
  }
  const contextMetadata: CheckoutContextResponse = await queryClient.ensureQueryData(
    queryBffContext(authenticatedUser?.userId || null),
  );
  const { fieldConstraints, pricing } = contextMetadata;

  const stripePriceId = extractPriceId(pricing);
  if (!stripePriceId) {
    return redirect(CheckoutPageRoute.PlanDetails);
  }

  const {
    valid,
    invalidRoute,
  } = await validateFormState({
    checkoutStep: 'BillingDetails',
    constraints: fieldConstraints,
    stripePriceId,
  });
  if (!valid && invalidRoute) {
    return redirect(invalidRoute);
  }

  // const checkoutSessionClientSecret = contextMetadata.checkoutIntent?.checkoutSessionClientSecret;
  const formStoreSecret = checkoutFormStore.getState().checkoutSessionClientSecret;
  const contextSecret = contextMetadata.checkoutIntent?.checkoutSessionClientSecret;
  const checkoutSessionClientSecret = formStoreSecret || contextSecret;
  if (!checkoutSessionClientSecret) {
    if (isEssentialsFlow()) {
      return redirect(EssentialsPageRoute.PlanDetails);
    }
    return redirect(CheckoutPageRoute.PlanDetails);
  }

  return null;
}

/**
 * Route loader for Billing Details Success page.
 *
 * @param {QueryClient} queryClient - TanStack Query client to ensure required context.
 * @returns {Promise<Response | null>} Redirects if prerequisites are not met;
 * otherwise null.
 */
async function billingDetailsSuccessLoader(queryClient: QueryClient): Promise<Response | null> {
  const authenticatedUser = getAuthenticatedUser();
  if (!authenticatedUser) {
    // If the user is NOT authenticated, redirect to PlanDetails Page.
    return redirect(CheckoutPageRoute.PlanDetails);
  }

  const contextMetadata: CheckoutContextResponse = await queryClient.ensureQueryData(
    queryBffContext(authenticatedUser?.userId || null),
  );

  const { checkoutIntent } = contextMetadata;

  const checkoutIntentType = checkoutFormStore.getState().checkoutSessionStatus?.type;

  if (checkoutIntentType !== 'complete' && !checkoutIntent?.existingSuccessfulCheckoutIntent) {
    return redirect(CheckoutPageRoute.PlanDetails);
  }

  return null;
}

/**
 * Page-specific route loaders mapped by checkout page
 */
const PAGE_LOADERS: Record<CheckoutPage, (queryClient: QueryClient) => Promise<Response | null>> = {
  PlanDetails: planDetailsLoader,
  PlanDetailsLogin: planDetailsLoginLoader,
  PlanDetailsRegister: planDetailsRegisterLoader,
  AccountDetails: accountDetailsLoader,
  BillingDetails: billingDetailsLoader,
  BillingDetailsSuccess: billingDetailsSuccessLoader,
};

/**
 * Factory that creates the checkout stepper loader handling both `/:step` and `/:step/:substep`.
 *
 * It determines the current step/substep from the route params, looks up the matching page details,
 * and delegates to the page-specific loader (see PAGE_LOADERS). If the route is invalid, it returns null
 * so that the 404 boundary can take over.
 *
 * @param {QueryClient} queryClient - Provided for parity with other loader factories (unused here).
 * @returns {LoaderFunction} A loader that dispatches to page-specific loaders based on route params.
 */
const makeCheckoutStepperLoader: MakeRouteLoaderFunctionWithQueryClient = function makeRootLoader(queryClient) {
  return async function checkoutStepperLoader({ params = {} }) {
    const { currentStep, currentSubstep } = getStepFromParams(params);
    const pageDetails = getCheckoutPageDetails({ step: currentStep, substep: currentSubstep });
    if (!pageDetails) {
      // Invalid route, do nothing. 404 page should kick in automatically.
      return null;
    }
    const pageLoader = PAGE_LOADERS[pageDetails.name];
    return pageLoader(queryClient);
  };
};

export default makeCheckoutStepperLoader;
